(function () {
  'use strict';
  let config = window.TAHPlatformConfig || {};
  const surfaces = Array.from(document.querySelectorAll('[data-platform-auth]'));
  let client, customer = null, initialized = false, recovery = false;
  const callback = new URL(location.href).searchParams.has('code');
  const LOGIN = '/pages/signup?view=login';
  const resendDeadlines = new Map();
  const RESEND_INTERVAL = 60000;
  let settleReady;
  const ready = new Promise(resolve => { settleReady = resolve; });
  const api = { enabled: config.enabled === true, ready, customer: null, fetch: platformFetch };
  window.TAHAuth = api;

  function status(surface, message, error) {
    const node = surface.querySelector('[data-auth-status]');
    if (node) { node.textContent = message || ''; node.dataset.error = error ? 'true' : 'false'; }
  }
  function show(surface, panel) {
    surface.querySelectorAll('[data-platform-panel]').forEach(node => { node.hidden = node.dataset.platformPanel !== panel; });
    const promotion = surface.querySelector('[data-welcome-promotion]');
    if (promotion) {
      promotion.hidden = !['offer', 'signup', 'verify'].includes(panel);
      if (promotion.hidden) {
        const heading = surface.querySelector('[data-platform-panel="' + panel + '"] h2');
        if (heading) {
          heading.id = 'tah-welcome-' + panel + '-title';
          surface.setAttribute('aria-labelledby', heading.id);
        }
        surface.removeAttribute('aria-describedby');
      } else {
        surface.setAttribute('aria-labelledby', 'tah-welcome-title');
        surface.setAttribute('aria-describedby', 'tah-welcome-description');
      }
    }
    status(surface, '');
  }
  function resendKey(surface) {
    return (surface.dataset.verificationEmail || '').toLowerCase() + ':' + surface.dataset.verificationType;
  }
  function pendingSignup(surface, email) {
    surface.dataset.verificationEmail = email;
    surface.dataset.verificationType = 'signup';
    surface.querySelectorAll('[data-auth-email]').forEach(node => { node.textContent = email; });
    show(surface, 'verify');
    updateResend(surface);
  }
  function updateResend(surface) {
    const button = surface.querySelector('[data-auth-resend]');
    if (!button) return;
    const seconds = Math.max(0, Math.ceil(((resendDeadlines.get(resendKey(surface)) || 0) - Date.now()) / 1000));
    button.disabled = !client || !surface.dataset.verificationEmail || surface.dataset.verificationType !== 'signup'
      || surface.dataset.authBusy === 'true' || seconds > 0;
    button.textContent = seconds ? 'Resend code in ' + seconds + 's' : 'Resend verification code';
  }
  function startResendCooldown(surface) {
    resendDeadlines.set(resendKey(surface), Date.now() + RESEND_INTERVAL);
    surfaces.forEach(updateResend);
  }
  async function resendCode(surface) {
    if (!client || !surface.dataset.verificationEmail || surface.dataset.verificationType !== 'signup' || surface.dataset.authBusy === 'true'
      || (resendDeadlines.get(resendKey(surface)) || 0) > Date.now()) return;
    surface.dataset.authBusy = 'true';
    const verifyButton = surface.querySelector('[data-platform-form="verify"] [data-auth-submit]');
    if (verifyButton) verifyButton.disabled = true;
    updateResend(surface);
    status(surface, 'Sending a new verification code...');
    try {
      const email = surface.dataset.verificationEmail;
      const redirectTo = location.origin + LOGIN;
      const result = await client.auth.resend({ type: 'signup', email, options: { emailRedirectTo: redirectTo } });
      if (result.error) throw result.error;
      surface.querySelectorAll('[data-field="code"], [data-code-digit]').forEach(node => { node.value = ''; });
      startResendCooldown(surface);
      status(surface, 'A new code has been requested. Use the latest email code within 5 minutes.');
    } catch (error) {
      if (error?.code === 'over_email_send_rate_limit' || error?.code === 'over_request_rate_limit') startResendCooldown(surface);
      status(surface, errorMessage(error), true);
    } finally {
      surface.dataset.authBusy = 'false';
      if (verifyButton) verifyButton.disabled = false;
      updateResend(surface);
    }
  }
  function clearPrivateCache() {
    try {
      [localStorage, sessionStorage].forEach(storage => {
        Object.keys(storage).filter(key => key.startsWith('tokenly-dashboard-') || key.startsWith('tah-dashboard-'))
          .forEach(key => storage.removeItem(key));
      });
    } catch (_) { /* Storage can be unavailable in private browsing. */ }
  }
  function clearPrivateView() {
    document.querySelectorAll('[data-console-view], [data-platform-orders]').forEach(node => {
      node.replaceChildren(); node.textContent = 'Sign in to access your account.';
    });
    document.querySelector('#tokenly-modal-root')?.replaceChildren();
    clearPrivateCache();
  }
  let sessionRefreshFlight;
  const createFlights = new Map();
  async function guardedCreate(path, options) {
    const key = String(options.body || "");
    if (createFlights.has(key)) return (await createFlights.get(key)).clone();
    const flight = platformFetch(path, { ...options, tahCreateGuard: true });
    createFlights.set(key, flight);
    try { return (await flight).clone(); } finally {
      window.dispatchEvent(new Event("tokenly:refresh-keys"));
      setTimeout(() => createFlights.delete(key), 120000);
    }
  }
  async function platformFetch(path, options) {
    if (!api.enabled || !client) throw new Error('Platform sign-in is not connected yet.');
    const relative = path.replace(/^\/apps\/api-console\/api\//, '');
    if (!/^(session|orders|bootstrap|keys|dashboard|reveal|create-token|update-token|key-status|custom-topup)(\?[^#]*)?$/.test(relative)) {
      throw new Error('Unsupported account request.');
    }
    if (relative === 'create-token' && !options?.tahCreateGuard) return guardedCreate(path, options);
    const result = await client.auth.getSession();
    if (result.error || !result.data.session) throw new Error('Sign in to continue.');
    const isKeyRead = (!options?.method || options.method === 'GET') && relative === 'keys';
    const keySnapshot = 'tah-dashboard-key-snapshot-' + result.data.session.user.id;
    const headers = new Headers(options?.headers);
    headers.set('Authorization', 'Bearer ' + result.data.session.access_token);
    let response = await fetch(config.apiOrigin + '/platform/api/' + relative, {
      ...options, headers, credentials: 'omit', cache: 'no-store', redirect: 'error'
    });
    // Recover a rejected session once; concurrent reads share the same refresh.
    if (response.status === 401) {
      if (!sessionRefreshFlight) sessionRefreshFlight = client.auth.refreshSession().finally(() => { sessionRefreshFlight = null; });
      const refreshed = await sessionRefreshFlight;
      if (!refreshed.error && refreshed.data?.session) {
        headers.set('Authorization', 'Bearer ' + refreshed.data.session.access_token);
        response = await fetch(config.apiOrigin + '/platform/api/' + relative, {
          ...options, headers, credentials: 'omit', cache: 'no-store', redirect: 'error'
        });
      }
    }
    if (isKeyRead && response.ok) {
      try { sessionStorage.setItem(keySnapshot, await response.clone().text()); } catch (_) {}
    }
    if (options?.method && options.method !== 'GET') {
      try { sessionStorage.removeItem(keySnapshot); } catch (_) {}
    }
    if (response.ok && /^dashboard(\?|$)/.test(relative) && customer) {
      try {
        const payload = await response.clone().json();
        if (payload.data?.usageIntegrity?.version === 1) localStorage.setItem('tokenly-dashboard-integrity-v1-' + customer.id,
          JSON.stringify({ data: payload.data, cachedAt: Date.now() }));
      } catch (_) {}
    }
    if (response.status === 401) {
      clearPrivateView();
      api.customer = null;
      customer = null;
      syncIdentity();
      try { sessionStorage.removeItem(keySnapshot); } catch (_) {}
      document.querySelectorAll('[data-console-view], [data-platform-orders]').forEach(node => {
        node.replaceChildren();
        const message = document.createElement('p');
        message.textContent = 'Your session has expired. Sign in again to refresh your account data.';
        const link = document.createElement('a');
        link.href = LOGIN;
        link.textContent = 'Sign in again';
        node.append(message, link);
      });
      surfaces.forEach(surface => status(surface, 'Your session has expired. Sign in again.', true));
    }
    return response;
  }
  async function connect(session) {
    if (!session) { customer = null; api.customer = null; return null; }
    const response = await platformFetch('session');
    const payload = await response.json();
    if (!response.ok || !payload.customer || !/^[1-9]\d*$/.test(payload.customer.id)) {
      throw new Error(payload.error || 'Your account could not be connected. Please retry.');
    }
    customer = payload.customer; api.customer = customer; api.subject = session.user.id;
    syncIdentity();
    document.dispatchEvent(new CustomEvent("tah-identity-ready"));
    return customer;
  }
  function syncIdentity() {
    document.body.dataset.customerAuthenticated = customer ? 'true' : 'false';
    document.querySelectorAll('[data-platform-native-account]').forEach(node => { node.hidden = true; });
    document.querySelectorAll('[data-platform-guest]').forEach(node => { node.hidden = !!customer; });
    document.querySelectorAll('[data-platform-account]').forEach(node => { node.hidden = !customer; });
    if (!customer) return;
    document.querySelectorAll('[data-console-view]').forEach(node => { node.dataset.customerId = customer.id; });
    document.querySelectorAll('[data-platform-name]').forEach(node => { node.textContent = customer.name || customer.email; });
    document.querySelectorAll('[data-platform-id]').forEach(node => { node.textContent = 'ID ' + customer.id; });
    document.querySelectorAll('[data-platform-avatar]').forEach(node => { node.textContent = (customer.name || customer.email).slice(0, 1).toUpperCase(); });
    document.querySelectorAll('.console-account b').forEach(node => { node.textContent = customer.name || customer.email; });
    const welcomeName = String(customer.name || customer.email || '').trim();
    document.querySelectorAll('[data-platform-welcome]').forEach(node => { node.textContent = welcomeName ? `Welcome back, ${welcomeName}.` : 'Welcome back.'; });
    document.querySelectorAll('.console-account small').forEach(node => { node.textContent = 'ID ' + customer.id; });
    document.querySelectorAll('[data-platform-key-link]').forEach(node => { node.href = '/pages/dashboard'; });
    const popup = document.querySelector('[data-tah-welcome]');
    if (popup?.open) popup.close();
  }
  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
  }
  async function loadOrders() {
    const body = document.querySelector('[data-platform-orders-body]');
    if (!body) return;
    const response = await platformFetch('orders');
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Your orders could not be loaded.');
    const rows = Array.isArray(payload.data) ? payload.data : [];
    if (!rows.length) {
      body.innerHTML = '<tr><td colspan="5">No orders are connected to this account yet.</td></tr>';
      return;
    }
    body.innerHTML = rows.map(order => {
      const money = order.totalPriceSet?.shopMoney || {};
      const total = new Intl.NumberFormat(undefined, { style: 'currency', currency: money.currencyCode || 'USD' }).format(Number(money.amount || 0));
      const date = order.createdAt ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(order.createdAt)) : '—';
      const items = (order.lineItems?.nodes || []).map(item => `${item.title} × ${item.quantity}`).join(', ') || '—';
      return `<tr><td>${escapeHtml(order.name)}</td><td>${escapeHtml(date)}</td><td>${escapeHtml(items)}</td><td>${escapeHtml(String(order.displayFinancialStatus || '—').replaceAll('_', ' '))}</td><td>${escapeHtml(total)}</td></tr>`;
    }).join('');
  }
  async function finish(session) {
    if (!session) throw new Error('Confirm your email before signing in.');
    await connect(session);
    const response = await platformFetch('bootstrap', { method: 'POST' });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.error || 'Your dashboard could not be prepared. Please retry.');
    }
    location.assign('/pages/dashboard');
  }
  function errorMessage(error) {
    const code = error?.code;
    if (code === 'invalid_credentials') return 'Email or password is incorrect.';
    if (code === 'email_not_confirmed') return 'Check your email and confirm your account before signing in.';
    if (code === 'otp_expired') return 'This code is invalid or has expired. Click Resend verification code and use the code from the latest email.';
    if (code === 'over_email_send_rate_limit' || code === 'over_request_rate_limit') return 'Too many attempts. Please wait before trying again.';
    if (code === 'weak_password') return 'Choose a stronger password with at least 8 characters.';
    return error?.message || 'Sign-in could not be completed. Please retry.';
  }
  function value(form, field) {
    const digits = field === 'code' ? Array.from(form.querySelectorAll('[data-code-digit]')) : [];
    return digits.length ? digits.map(node => node.value).join('') : form.querySelector('[data-field="' + field + '"]')?.value || '';
  }
  function bindCodeBoxes(surface) {
    const digits = Array.from(surface.querySelectorAll('[data-code-digit]'));
    function distribute(text, index) {
      const code = text.replace(/[\s-]/g, '');
      if (!/^[0-9]+$/.test(code) || code.length > 6) {
        status(surface, 'Enter the 6-digit code from your latest email.', true);
        return false;
      }
      const start = code.length === 6 ? 0 : index;
      if (start + code.length > 6) return false;
      if (code.length === 6) digits.forEach(node => { node.value = ''; });
      Array.from(code).forEach((digit, offset) => { digits[start + offset].value = digit; });
      digits[Math.min(start + code.length, 5)]?.focus();
      return true;
    }
    digits.forEach((input, index) => {
      input.addEventListener('focus', () => input.select());
      input.addEventListener('input', event => {
        if (event.isComposing || !input.value) return;
        const text = input.value;
        input.value = '';
        distribute(text, index);
      });
      input.addEventListener('paste', event => {
        event.preventDefault();
        distribute(event.clipboardData?.getData('text') || '', index);
      });
      input.addEventListener('keydown', event => {
        if (event.key === 'Backspace' && !input.value && index > 0) {
          event.preventDefault(); digits[index - 1].value = ''; digits[index - 1].focus();
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          digits[Math.max(0, Math.min(5, index + (event.key === 'ArrowLeft' ? -1 : 1)))]?.focus();
        }
      });
    });
  }
  async function submit(surface, form) {
    const mode = form.dataset.platformForm;
    const email = value(form, 'email').trim();
    const password = value(form, 'password');
    if ((mode === 'signup' || mode === 'reset') && password !== value(form, 'confirm')) throw new Error('Passwords do not match.');
    const redirectTo = location.origin + LOGIN;
    let result;
    if (mode === 'login') {
      result = await client.auth.signInWithPassword({ email, password });
      if (result.error?.code === 'email_not_confirmed') {
        form.querySelectorAll('[type="password"]').forEach(node => { node.value = ''; });
        pendingSignup(surface, email);
        status(surface, 'Your registration is not verified yet. Enter your registration code, or click Resend verification code.', true);
        return;
      }
    } else if (mode === 'signup') {
      result = await client.auth.signUp({ email, password, options: { emailRedirectTo: redirectTo } });
      const existingAccount = ['user_already_exists', 'email_exists'].includes(result.error?.code)
        || (!result.error && result.data?.user && Array.isArray(result.data.user.identities)
          && result.data.user.identities.length === 0);
      if (existingAccount) {
        form.querySelectorAll('[type="password"]').forEach(node => { node.value = ''; });
        show(surface, 'login');
        const loginEmail = surface.querySelector('[data-platform-form="login"] [data-field="email"]');
        if (loginEmail) loginEmail.value = email;
        status(surface, 'This email is already registered. Please sign in, or use Forgot password to reset your password.', true);
        return;
      }
    } else if (mode === 'verify') {
      if (!surface.dataset.verificationEmail || surface.dataset.verificationType !== 'signup') {
        throw new Error('Start email registration before verifying your account.');
      }
      const token = value(form, 'code').trim();
      if (!/^[0-9]{6}$/.test(token)) throw new Error('Enter all 6 digits from your latest verification email.');
      // Supabase's email OTP verifier confirms ownership; this UI only requests signup codes.
      result = await client.auth.verifyOtp({
        email: surface.dataset.verificationEmail,
        token,
        type: 'email'
      });
    } else if (mode === 'recovery') {
      result = await client.auth.resetPasswordForEmail(email, { redirectTo });
    } else if (mode === 'reset') {
      if (!recovery) throw new Error('Open the secure reset link from your email first.');
      result = await client.auth.updateUser({ password });
    } else { throw new Error('Unsupported sign-in action.'); }
    if (result.error) throw result.error;
    // Secrets are never written into messages, URLs or application logs.
    form.querySelectorAll('[type="password"], [data-field="code"], [data-code-digit]').forEach(node => { node.value = ''; });
    if (mode === 'recovery') {
      status(surface, 'If an account exists for this email, a password reset link has been sent.'); return;
    }
    if (mode === 'signup' && !result.data.session) {
      pendingSignup(surface, email);
      startResendCooldown(surface);
      return;
    }
    const session = result.data?.session || (await client.auth.getSession()).data.session;
    await finish(session);
  }
  function bindEnglishValidation(surface) {
    surface.querySelectorAll('[data-platform-form] input').forEach(input => {
      const clear = () => input.setCustomValidity('');
      input.addEventListener('input', clear);
      input.addEventListener('change', clear);
      // Native validation messages otherwise follow the browser's language.
      input.addEventListener('invalid', () => {
        clear();
        const validity = input.validity;
        let message = '';
        if (validity.valueMissing) {
          if (input.type === 'email') message = 'Please enter your email address.';
          else if (input.type === 'password') message = 'Please enter your password.';
          else if (input.hasAttribute('data-code-digit')) message = 'Please enter the 6-digit verification code.';
          else message = 'Please fill out this field.';
        } else if (validity.typeMismatch && input.type === 'email') {
          message = 'Please enter a valid email address.';
        } else if (validity.tooShort) {
          message = 'Please use at least ' + input.minLength + ' characters.';
        } else if (validity.tooLong) {
          message = 'Please use no more than ' + input.maxLength + ' characters.';
        } else if (validity.patternMismatch) {
          message = input.hasAttribute('data-code-digit') ? 'Please enter a digit from 0 to 9.' : 'Please use the requested format.';
        } else if (!validity.valid) {
          message = 'Please enter a valid value.';
        }
        input.setCustomValidity(message);
      });
    });
  }
  function bind() {
    surfaces.forEach(surface => {
      bindCodeBoxes(surface);
      bindEnglishValidation(surface);
      surface.addEventListener('click', async event => {
        const resend = event.target.closest('[data-auth-resend]');
        if (resend) { if (!resend.disabled) await resendCode(surface); return; }
        const toggle = event.target.closest('[data-auth-show]');
        if (toggle) {
          if (surface.dataset.authBusy === 'true') return;
          show(surface, toggle.dataset.authShow);
          return;
        }
        const provider = event.target.closest('[data-platform-provider]');
        if (!provider || provider.disabled) return;
        provider.disabled = true; status(surface, 'Opening secure sign-in...');
        try {
          const name = provider.dataset.platformProvider;
          const result = await client.auth.signInWithOAuth({ provider: name, options: {
            redirectTo: location.origin + LOGIN,
            ...(name === 'github' ? { scopes: 'read:user user:email', queryParams: { prompt: 'select_account' } } : {})
          } });
          if (result.error) throw result.error;
        } catch (error) { status(surface, errorMessage(error), true); provider.disabled = false; }
      });
      surface.querySelectorAll('[data-platform-form]').forEach(form => {
        form.addEventListener('submit', async event => {
          event.preventDefault();
          if (!client || !form.reportValidity() || surface.dataset.authBusy === 'true') return;
          surface.dataset.authBusy = 'true';
          form.dataset.busy = 'true';
          const button = form.querySelector('[data-auth-submit]'); button.disabled = true;
          status(surface, 'Please wait...');
          try { await submit(surface, form); } catch (error) { status(surface, errorMessage(error), true); }
          finally { button.disabled = false; form.dataset.busy = 'false'; surface.dataset.authBusy = 'false'; updateResend(surface); }
        });
      });
    });
    setInterval(() => surfaces.forEach(updateResend), 1000);
    document.querySelectorAll('[data-platform-signout]').forEach(button => button.addEventListener('click', async event => {
      event.preventDefault(); clearPrivateView();
      try { const result = await client.auth.signOut(); if (result.error) throw result.error; location.assign(LOGIN); }
      catch (_) { surfaces.forEach(surface => status(surface, 'Sign-out failed. Please retry.', true)); location.reload(); }
    }));
  }
  const GOOGLE_CLIENT_ID = '649604195611-qbobt8tc4pb37q0as4i7pqaq02mkjrt1.apps.googleusercontent.com';
  let googleLibrary;
  function loadGoogleIdentity() {
    if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
    if (!googleLibrary) googleLibrary = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const fail = message => {
        clearTimeout(timer); script.remove(); googleLibrary = null;
        reject(new Error(message));
      };
      const timer = setTimeout(() => fail('Google connection timed out.'), 12000);
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = () => {
        if (!window.google?.accounts?.id) return fail('Google sign-in could not load.');
        clearTimeout(timer); resolve(window.google.accounts.id);
      };
      script.onerror = () => fail('Cannot connect to Google. Check your network and retry.');
      document.head.appendChild(script);
    });
    return googleLibrary;
  }
  async function mountGoogleButtons() {
    const googleId = await loadGoogleIdentity();
    googleId.initialize({
      client_id: GOOGLE_CLIENT_ID,
      ux_mode: 'popup',
      auto_select: false,
      button_auto_select: false,
      use_fedcm_for_button: false,
      callback: async response => {
        const surface = document.querySelector('[data-google-pending]') || surfaces.find(node => node.getBoundingClientRect().height > 0);
        if (!surface || !response.credential) return;
        status(surface, 'Completing secure sign-in...');
        try {
          const result = await client.auth.signInWithIdToken({ provider: 'google', token: response.credential });
          if (result.error) throw result.error;
          await finish(result.data.session);
        } catch (error) { status(surface, errorMessage(error), true); }
        finally { surface.removeAttribute('data-google-pending'); }
      }
    });
    surfaces.forEach(surface => {
      surface.querySelectorAll('[data-platform-provider="google"]').forEach(button => {
        if (button.hidden || button.parentElement.querySelector('.tah-google-official-button')) return;
        const mount = document.createElement('div');
        mount.className = 'tah-google-official-button';
        const template = button.parentElement.querySelector('[data-platform-provider="github"]') || button;
        const buttonStyle = getComputedStyle(template);
        const buttonHeight = button.offsetHeight || 44;
        const buttonWidth = button.offsetWidth || 378;
        mount.style.cssText = 'position:relative;width:100%;margin:0 0 12px;isolation:isolate';
        mount.style.height = buttonHeight + 'px';
        const appearance = template.cloneNode(true);
        appearance.innerHTML = button.innerHTML;
        appearance.removeAttribute('data-platform-provider');
        appearance.removeAttribute('id');
        appearance.removeAttribute('disabled');
        appearance.setAttribute('aria-hidden', 'true');
        appearance.tabIndex = -1;
        appearance.style.cssText = 'width:100%;height:100%;margin:0;pointer-events:none;display:flex;align-items:center;justify-content:center';
        appearance.style.font = buttonStyle.font;
        appearance.style.borderRadius = buttonStyle.borderRadius;
        appearance.querySelectorAll('[data-provider-status]').forEach(node => node.remove());
        const official = document.createElement('div');
        official.style.cssText = 'position:absolute;inset:0;opacity:0.001;overflow:hidden;z-index:1';
        // Keep the official Google control as the full click and keyboard target.
        const scaleY = buttonHeight / 32;
        official.style.height = '32px';
        official.style.transformOrigin = 'top left';
        official.style.transform = 'scaleY(' + scaleY + ')';
        mount.append(appearance, official);
        button.after(mount);
        mount.addEventListener('pointerdown', () => {
          surfaces.forEach(node => node.removeAttribute('data-google-pending'));
          surface.setAttribute('data-google-pending', 'true');
        }, true);
        mount.addEventListener('focusin', () => { appearance.style.outline = '2px solid #3155ff'; appearance.style.outlineOffset = '2px'; });
        mount.addEventListener('focusout', () => { appearance.style.outline = ''; });
        googleId.renderButton(official, {
          type: 'standard', theme: 'outline', size: 'medium',
          text: 'continue_with', shape: 'rectangular', locale: 'en',
          width: Math.min(400, Math.round(buttonWidth))
        });
        button.hidden = true;
        button.style.display = 'none';
      });
    });
  }

  function keepDashboardValues() {
    if (!customer || !api.subject) return;
    const cacheKey = 'tah-dashboard-integrity-values-v1-' + api.subject;
    const root = document.querySelector('[data-console-view="dashboard"]');
    if (!root) return;
    let saved = {};
    try { saved = JSON.parse(sessionStorage.getItem(cacheKey) || '{}'); } catch (_) {}
    function restore() {
      root.querySelectorAll('[data-dashboard-value]').forEach(node => {
        const name = node.dataset.dashboardValue;
        const value = node.textContent.trim();
        if (/Loading|Waiting|Syncing|^\s*$/.test(value)) {
          if (saved[name]) node.textContent = saved[name];
        } else if (value !== '—') saved[name] = value;
      });
      try { sessionStorage.setItem(cacheKey, JSON.stringify(saved)); } catch (_) {}
    }
    restore();
    const observer = new MutationObserver(restore);
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  }
/* Login connection recovery, 2026-10-23. Never discard a session on a network error. */
function offerConnectionRetry(message, action) {
  surfaces.forEach(surface => {
    status(surface, message, true);
    let button = surface.querySelector('[data-auth-connection-retry]');
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.dataset.authConnectionRetry = '';
      button.textContent = 'Retry connection';
      button.style.cssText = 'display:block;margin:12px 0;padding:10px 16px;border:1px solid #3155ff;border-radius:8px;background:white;color:#3155ff;cursor:pointer';
      const target = surface.querySelector('[data-auth-status]');
      if (target) target.after(button); else surface.append(button);
    }
    button.onclick = () => { button.remove(); action(); };
  });
}
async function loginBootstrapFetch(url, options) {
  let lastError;
  for (let attempt = 0; attempt < 3; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {...options, signal: controller.signal});
      if (response.ok || response.status < 500) return response;
      lastError = new Error('Sign-in service is temporarily unavailable.');
    } catch (error) { lastError = error; }
    finally { clearTimeout(timer); }
    if (attempt < 2) {
      surfaces.forEach(surface => status(surface, 'Reconnecting to sign-in service...'));
      await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
    }
  }
  throw lastError;
}
function connectGoogleIndependently() {
  surfaces.forEach(surface => surface.querySelectorAll('[data-platform-provider="google"]').forEach(button => {
    button.disabled = true;
    const badge = button.querySelector('[data-provider-status]');
    if (badge) { badge.hidden = false; badge.textContent = 'Connecting...'; }
  }));
  return mountGoogleButtons().catch(() => { if (customer) return;
    offerConnectionRetry('Google could not connect. Email and GitHub sign-in are still available.', connectGoogleIndependently);
  });
}
function connectProvidersIndependently(){
  return (async()=>{const settingsResponse=await loginBootstrapFetch(config.supabaseUrl+"/auth/v1/settings",{headers:{apikey:config.publishableKey},credentials:"omit",redirect:"error"});if(!settingsResponse.ok)throw new Error("Authentication settings are unavailable.");const providerSettings=await settingsResponse.json();if(surfaces.forEach(surface=>{surface.querySelectorAll("[data-auth-submit]").forEach(button=>{button.disabled=!1}),surface.querySelectorAll("[data-platform-provider]").forEach(button=>{const name=button.dataset.platformProvider;button.disabled=!(config.providers?.[name]&&providerSettings.external?.[name]);const badge=button.querySelector("[data-provider-status]");badge&&(badge.hidden=!button.disabled)})}),config.providers?.google&&providerSettings.external?.google)connectGoogleIndependently()})().catch(()=>{
    if (customer) return; offerConnectionRetry('Provider connection failed. You can still sign in with email, or retry.', connectProvidersIndependently);
  });
}

  async function initialize() {
    bind();
    surfaces.forEach(surface => {
      status(surface, 'Connecting to sign-in service...');
      surface.querySelectorAll('[data-provider-status]').forEach(badge => { badge.textContent = 'Connecting...'; });
    });
    if (window.TAHPlatformConfigUrl === 'https://backend.aitokenshop.ai/platform/api/config') {
      const response = await loginBootstrapFetch(window.TAHPlatformConfigUrl, {
        credentials: 'omit', cache: 'no-store', redirect: 'error'
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Sign-in is temporarily unavailable. Please retry.');
      config = payload;
      api.enabled = config.enabled === true;
    }
    if (!api.enabled) { surfaces.forEach(surface => status(surface, 'Sign-in configuration is not complete yet.')); return; }
    if (config.supabaseUrl !== 'https://drubymwmhdsygwiammvj.supabase.co' || config.apiOrigin !== 'https://backend.aitokenshop.ai'
      || !config.publishableKey || !window.TAHCreateAuthClient) throw new Error('Sign-in configuration is incomplete.');
    client = window.TAHCreateAuthClient(config.supabaseUrl, config.publishableKey, {
      auth: { flowType: 'pkce', persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
    });
    client.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') { recovery = true; surfaces.forEach(surface => show(surface, 'reset')); }
      if (!initialized) return;
      // Defer SDK calls until its auth-event lock has been released.
      setTimeout(() => {
        if (event === 'SIGNED_OUT' || (session && customer && api.subject && session.user.id !== api.subject)) {
          clearPrivateView(); location.reload();
        }
      }, 0);
    });
    if (surfaces.length) {
      surfaces.forEach(surface => {
        surface.querySelectorAll('[data-auth-submit]').forEach(button => { button.disabled = false; });
        status(surface, '');
      });
      connectProvidersIndependently();
    }
    const sessionResult = await client.auth.getSession();
    if (sessionResult.error) throw sessionResult.error;
    const session = sessionResult.data.session;
    api.subject = session?.user.id || null;
    await connect(session);
    syncIdentity();
    if (session && document.querySelector('[data-platform-orders-body]')) {
      try { await loadOrders(); }
      catch (error) {
        const body = document.querySelector('[data-platform-orders-body]');
        if (body) body.innerHTML = `<tr><td colspan="5">${escapeHtml(errorMessage(error))}</td></tr>`;
      }
    }
    if (session && recovery) { surfaces.forEach(surface => show(surface, 'reset')); }
    else if (session && callback) { await finish(session); }
    else if (session) { surfaces.forEach(surface => show(surface, 'signed-in')); }
    if (!session && document.querySelector('[data-console-view], [data-platform-orders]')) {
      clearPrivateView(); location.replace(LOGIN);
    }
    initialized = true;
  }
  initialize().catch(error => {
    offerConnectionRetry('Could not connect to sign-in. Please retry. Your saved session has been kept.', () => location.reload());
    window.addEventListener('online', () => location.reload(), { once: true });
  }).finally(() => { settleReady(!!customer); document.dispatchEvent(new CustomEvent('tah-auth-ready')); });
})();

// Refresh verified usage whenever the account's key roster changes.
(function () {
  let roster = null, timer;
  window.addEventListener('tokenly:customer-keys-loaded', function (event) {
    const next = (Array.isArray(event.detail) ? event.detail : []).map(k => String(k.id) + ':' + k.status).sort().join('|');
    if (next === roster) return;
    roster = next;
    clearTimeout(timer);
    timer = setTimeout(function () {
      document.querySelector('[data-console-refresh]')?.click();
    }, 100);
  });
})();

(function () {
  const auth = window.TAHAuth;
  if (!auth) return;
  const original = auth.fetch;
  const summaries = new Map();
  let pendingRow = null, pendingName = '', pollTimer;
  const money = value => '$ ' + (Number(value) / 10).toFixed(2);
  function notice(text) {
    let node = document.querySelector('[data-key-operation-status]');
    if (!node) {
      node = document.createElement('p'); node.dataset.keyOperationStatus = '';
      node.setAttribute('role', 'status'); node.setAttribute('aria-live', 'polite');
      document.querySelector('[data-token-table]')?.before(node);
    }
    if (node) node.textContent = text;
  }
  function paint() {
    document.querySelectorAll('[data-token-row]').forEach(row => {
      const item = summaries.get(String(row.dataset.fulfillmentId));
      if (!item?.usage) return;
      for (const [field, value] of Object.entries({sharedTotal:item.totalLimit,used:item.usage.used,remaining:item.usage.remaining})) {
        const cell = row.querySelector('[data-token-stat="' + field + '"]');
        if (cell && value != null && /Loading|Waiting|Unavailable/.test(cell.textContent)) cell.textContent = money(value);
      }
      const sync = row.querySelector('[data-token-stat="sync"]');
      if (sync && /Waiting/.test(sync.textContent)) sync.textContent = item.status === 'ready' ? 'Balance available' : 'Preparing quota';
    });
  }
  function initialRow(payload) {
    pendingName = payload.name || 'API token';
    const body = document.querySelector('[data-token-table] tbody');
    const template = body?.querySelector('[data-token-row]');
    if (!body || !template) return;
    const row = template.cloneNode(true);
    row.removeAttribute('data-fulfillment-id'); row.dataset.localCreation = 'true';
    row.dataset.secret = ''; row.dataset.last4 = ''; row.dataset.tokenStatus = 'pending';
    row.querySelector('td:nth-child(2) b').textContent = pendingName;
    const status = row.querySelector('td:nth-child(3)'); if(status) status.textContent='Creating';
    const secret = row.querySelector('td:nth-child(4)'); if(secret) secret.textContent='Assigning API key...';
    row.querySelectorAll('button,input').forEach(node => node.disabled=true);
    const used=row.querySelector('[data-token-stat="used"]'); if(used) used.textContent='$ 0.00';
    const sync=row.querySelector('[data-token-stat="sync"]'); if(sync) sync.textContent='Submitting request';
    body.prepend(row); pendingRow=row;
    row.scrollIntoView({block:'nearest'});
  }
  function poll() {
    clearTimeout(pollTimer);
    pollTimer=setTimeout(()=>window.dispatchEvent(new Event('tokenly:refresh-keys')),2000);
  }
  auth.fetch = async function(path, options) {
    const isCreate=String(path).includes('/create-token');
    if (isCreate) {
      const payload=JSON.parse(options?.body||'{}');
      if (!pendingRow) initialRow(payload);
      document.querySelector('#tokenly-modal-root')?.replaceChildren();
      notice('Creating your API key. This request has been submitted; please do not submit it again.');
    }
    try {
      const response=await original(path,options);
      if (String(path).match(/\/keys(?:\?|$)/) && response.ok) {
        const value=await response.clone().json();
        (value.data||[]).forEach(item=>summaries.set(String(item.id),item));
        setTimeout(paint,0);
      }
      if (isCreate) {
        const value=await response.clone().json().catch(()=>({}));
        if(response.ok && value.token?.id) {
          notice(value.status==='ready'?'API key created. Updating this row...':'Request accepted. Assigning the key and applying your account quota...');
          pendingRow?.remove(); pendingRow=null;
          window.dispatchEvent(new Event('tokenly:refresh-keys')); poll();
        } else {
          notice(value.error||'Creation could not be confirmed. Checking the existing request...');
          poll();
        }
      }
      return response;
    } catch(error) {
      if(isCreate) {notice('Connection interrupted. Checking whether your API key was created; please do not resubmit.');poll();}
      throw error;
    }
  };
  window.addEventListener('tokenly:customer-keys-loaded',event=>{
    const rows=Array.isArray(event.detail)?event.detail:[];
    rows.forEach(item=>summaries.set(String(item.id),item));
    const item=rows.find(item=>item.orderName===pendingName);
    if(item) {
      pendingRow?.remove();pendingRow=null;
      if(item.status==='ready') notice('API key ready. Your account balance is available.');
      else if(item.status==='failed') notice('API key setup failed. Please check the request status before retrying.');
      else {notice('API key setup in progress. Applying account quota...');poll();}
    }
    setTimeout(paint,0);
  });
  const table=document.querySelector('[data-token-table]');
  if(table) new MutationObserver(paint).observe(table,{childList:true,subtree:true});
  document.addEventListener('click',event=>{
    if(event.target.closest('[data-token-action="create"]')) notice('Opening the API key form...');
  },true);
})();
